import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UsageMeter } from './UsageMeter';
import { ImpersonationBanner } from './ImpersonationBanner';
import { AppSwitcher } from './AppSwitcher';
import { LATEST_VERSION } from '../data/changelog';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Sparkles, LayoutDashboard, FileText, Wand2, Download,
  Settings, LogOut, Menu, X, ChevronLeft, User, LayoutTemplate,
  CreditCard, Shield, CalendarDays, Building2, Plug, BookOpen, Megaphone, Mail,
  Archive, Briefcase, GraduationCap, BarChart3, Target, PenSquare, LifeBuoy, Link2, CalendarRange, Rocket, Send, MessageCircle, LayoutPanelLeft, Clapperboard, Image, Palette, Video, Handshake, FileSignature
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const getNavItems = (isAdmin) => {
  const items = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/compose', label: 'Compose', icon: PenSquare },
    { href: '/generate', label: 'Generate', icon: Wand2 },
    { href: '/url-generator', label: 'URL Generator', icon: Link2 },
    { href: '/weekly-themes', label: 'Weekly Themes', icon: CalendarRange },
    { href: '/ai-manager', label: 'AI Post Manager', icon: Sparkles },
    { href: '/studio/blog', label: 'AI Blog', icon: BookOpen },
    { href: '/studio/ad', label: 'AI Ads', icon: Megaphone },
    { href: '/studio/email', label: 'AI Email', icon: Mail },
    { href: '/studio/creative', label: 'AI Creative Studio', icon: Image },
    { href: '/video-studio', label: 'Video Studio', icon: Video },
    { href: '/brand-kit', label: 'Brand Kits', icon: Palette },
    { href: '/email', label: 'Email Marketing', icon: Send },
    { href: '/comment-automation', label: 'Comment Automation', icon: MessageCircle },
    { href: '/funnels', label: 'Funnel Builder', icon: LayoutPanelLeft },
    { href: '/templates', label: 'Templates', icon: LayoutTemplate },
    { href: '/posts', label: 'My Posts', icon: FileText },
    { href: '/campaigns', label: 'Campaign Manager', icon: Target },
    { href: '/calendar', label: 'Calendar', icon: CalendarDays },
    { href: '/analytics', label: 'Analytics', icon: BarChart3 },
    { href: '/archive', label: 'Archive', icon: Archive },
    { href: '/clients', label: 'Clients', icon: Briefcase },
    { href: '/proposals', label: 'Proposals', icon: FileSignature },
    { href: '/style-profiles', label: 'Style Profiles', icon: Sparkles },
    { href: '/organizations', label: 'Organizations', icon: Building2 },
    { href: '/connections', label: 'Connections', icon: Plug },
    { href: '/export', label: 'Export', icon: Download },
    { href: '/subscription', label: 'Subscription', icon: CreditCard },
    { href: '/affiliate', label: 'Partner Program', icon: Handshake },
    { href: '/tutorials', label: 'Tutorials', icon: GraduationCap },
    { href: '/changelog', label: "What's new", icon: Rocket },
    { href: '/support', label: 'Support', icon: LifeBuoy },
    { href: '/settings', label: 'Settings', icon: Settings },
  ];
  
  if (isAdmin) {
    items.push({ href: '/admin', label: 'Admin', icon: Shield, isAdmin: true });
    items.push({ href: '/admin/tutorial-scripts', label: 'Tutorial Scripts', icon: Clapperboard, isAdmin: true });
  }
  
  return items;
};

export const DashboardLayout = ({ children }) => {
  const { user, logout, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bundleApps, setBundleApps] = useState([]);

  const navItems = getNavItems(user?.is_admin);

  // Load sibling apps for the App Switcher (registered SSO apps with a launch URL).
  useEffect(() => {
    let active = true;
    api.get('/sso/apps')
      .then((res) => { if (active) setBundleApps(res.data || []); })
      .catch(() => { /* switcher just shows the current app */ });
    return () => { active = false; };
  }, [api]);

  // "What's new" indicator — green dot on the Changelog nav item until the
  // user visits /changelog (which writes the LATEST_VERSION to localStorage).
  // Listens for the 'changelog-seen' event so the dot disappears immediately
  // on visit (no reload required).
  const [hasUnseenChangelog, setHasUnseenChangelog] = useState(() => {
    try {
      return localStorage.getItem('changelog_seen_version') !== LATEST_VERSION;
    } catch { return false; }
  });
  useEffect(() => {
    const sync = () => {
      try {
        setHasUnseenChangelog(localStorage.getItem('changelog_seen_version') !== LATEST_VERSION);
      } catch {
        // localStorage can throw in restricted contexts (private mode / iframes).
        // Silently keep the previous state — the dot just won't auto-clear.
      }
    };
    window.addEventListener('changelog-seen', sync);
    return () => window.removeEventListener('changelog-seen', sync);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="dashboard-layout">
      <ImpersonationBanner />
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 glass border-b border-zinc-200/50 px-4 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold tracking-tight">PostToSocial</span>
        </Link>
        <div className="flex items-center gap-1">
          <AppSwitcher apps={bundleApps} trigger="icon" />
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-zinc-200 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 px-4 flex items-center justify-between border-b border-zinc-100">
            <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
              <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight">PostToSocial</span>
            </Link>
            <AppSwitcher apps={bundleApps} trigger="icon" onNavigate={() => setSidebarOpen(false)} />
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {bundleApps.length > 0 && (
                <div className="pb-1 mb-1 border-b border-zinc-100">
                  <AppSwitcher apps={bundleApps} trigger="nav" onNavigate={() => setSidebarOpen(false)} />
                </div>
              )}
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <div
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? item.isAdmin ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'
                          : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1">{item.label}</span>
                      {item.href === '/changelog' && hasUnseenChangelog && (
                        <span
                          className="inline-block w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100"
                          title="New updates available"
                          data-testid="changelog-new-dot"
                        />
                      )}
                    </div>
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-3 border-t border-zinc-100">
            <UsageMeter />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-auto py-3 px-3 hover:bg-zinc-100"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
